import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const TOAST_DURATION = 2600;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NOTIFY_KEY = 'tmc-notify';

/**
 * Collection page behaviour, also used by the athlete page: size panel and direct add on phones,
 * size bar on desktop, "notify me" panel (collection pages only), toast. Panels are native modal dialogs, which provide Escape, the focus trap and the
 * return of focus to the trigger.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} sizeDialog
 * @property {HTMLImageElement} sizeThumb
 * @property {HTMLElement} sizeNameFirst
 * @property {HTMLElement} sizeNameRest
 * @property {HTMLElement} sizePrice
 * @property {HTMLElement} sizeList
 * @property {HTMLElement} sizeAlert
 * @property {HTMLTemplateElement} sizeError
 * @property {HTMLButtonElement} sizeSubmit
 * @property {HTMLDialogElement} [notifyDialog] - Absent on the athlete page
 * @property {HTMLImageElement} notifyThumb
 * @property {HTMLElement} notifyName
 * @property {HTMLInputElement} notifyTags
 * @property {HTMLInputElement} notifyEmail
 * @property {HTMLElement} notifyAlert
 * @property {HTMLTemplateElement} notifyError
 * @property {HTMLElement} notifySuccess
 * @property {HTMLTemplateElement} notifyDone
 * @property {HTMLElement} toast
 *
 * @extends {Component<Refs>}
 */
class TmCollection extends Component {
  requiredRefs = ['sizeDialog', 'toast'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #toastTimeout;

  /** @type {HTMLElement | null} */
  #sizeCard = null;

  /** @type {string} */
  #notifyHandle = '';

  /** @type {Set<string>} */
  #notified = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });
    // Capture: this must run before the anti-spam script bound to the form itself.
    this.addEventListener('submit', this.#onSubmit, { signal, capture: true });
    this.refs.notifyEmail?.addEventListener('input', () => this.#setEmailError(false), { signal });
    this.#showPosted();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    clearTimeout(this.#toastTimeout);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    // A click on the backdrop lands on the dialog element itself.
    if (target instanceof HTMLDialogElement) {
      target.close();
      return;
    }

    const button = target.closest?.('button');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const card = button.closest('.tmc-card');

    if (button.matches('[data-tmc-close]')) {
      button.closest('dialog')?.close();
    } else if (button.matches('[data-tmc-open-sizes]') && card instanceof HTMLElement) {
      this.#openSizes(card);
    } else if (button.matches('[data-tmc-add]') && card instanceof HTMLElement) {
      this.#add(button.dataset.variantId, card.dataset.title ?? '');
    } else if (button.matches('.tmc-card__sizes .tmc-size') && card instanceof HTMLElement) {
      this.#addSize(button, card);
    } else if (button.matches('.tmc-sheet__sizes .tmc-size')) {
      this.#pickSize(button);
    } else if (button.matches('[data-tmc-confirm-size]')) {
      this.#confirmSize();
    } else if (button.matches('[data-tmc-notify]')) {
      this.#openNotify(button);
    }
  };

  /** @param {HTMLElement} card */
  #openSizes(card) {
    const { sizeDialog, sizeThumb, sizeNameFirst, sizeNameRest, sizePrice, sizeList, sizeAlert } = this.refs;

    this.#sizeCard = card;
    if (card.dataset.thumb) sizeThumb.src = card.dataset.thumb;
    sizeNameFirst.textContent = card.dataset.nameFirst ?? '';
    sizeNameRest.textContent = card.dataset.nameRest ?? '';
    sizePrice.textContent = card.dataset.price ?? '';
    sizeAlert.replaceChildren();

    sizeList.replaceChildren(
      ...[...card.querySelectorAll('.tmc-card__sizes .tmc-size')].map((source) => {
        const button = /** @type {HTMLButtonElement} */ (source.cloneNode(true));
        button.setAttribute('aria-label', button.dataset.size ?? '');
        button.setAttribute('aria-pressed', 'false');
        return button;
      })
    );

    sizeDialog.showModal();
  }

  /** @param {HTMLButtonElement} button */
  #pickSize(button) {
    for (const size of this.refs.sizeList.querySelectorAll('.tmc-size')) {
      size.setAttribute('aria-pressed', String(size === button));
    }
    this.refs.sizeAlert.replaceChildren();
  }

  async #confirmSize() {
    const { sizeList, sizeAlert, sizeError, sizeDialog, sizeSubmit } = this.refs;
    const picked = sizeList.querySelector('[aria-pressed="true"]');

    if (!(picked instanceof HTMLElement)) {
      sizeAlert.replaceChildren(sizeError.content.cloneNode(true));
      return;
    }

    sizeSubmit.disabled = true;
    const added = await this.#add(picked.dataset.variantId, this.#label(this.#sizeCard, picked));
    sizeSubmit.disabled = false;
    if (added) sizeDialog.close();
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {HTMLElement} card
   */
  async #addSize(button, card) {
    if (!(await this.#add(button.dataset.variantId, this.#label(card, button)))) return;

    button.classList.add('is-picked');
    setTimeout(() => button.classList.remove('is-picked'), TOAST_DURATION);
  }

  /**
   * @param {HTMLElement | null} card
   * @param {HTMLElement} size
   */
  #label(card, size) {
    return `${card?.dataset.title ?? ''} · ${size.dataset.size ?? ''}`;
  }

  /**
   * Adds the variant through the cart API and hands the result to Horizon's cart listeners
   * (cart icon and drawer), following the contract of product-form.js.
   * @param {string | undefined} variantId
   * @param {string} label - Second line of the toast.
   * @returns {Promise<boolean>} Whether the variant was added.
   */
  async #add(variantId, label) {
    if (!variantId) return false;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    if (sectionIds.length > 0) formData.set('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const config = fetchConfig('javascript', { body: formData });
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((result) => result.json());

      const failed = Boolean(response.status);
      const cart = await fetch(`${Theme.routes.cart_url}.json`).then((result) => result.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: failed,
          items: cart.items,
          source: 'tm-collection',
          itemCount: 1,
          sections: failed ? undefined : response.sections,
        },
      });

      if (failed) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#toast(this.dataset.error, response.description || response.message);
        return false;
      }

      this.#toast(this.dataset.added, label);
      return true;
    } catch (error) {
      deferred.reject(error);
      this.#toast(this.dataset.error, '');
      return false;
    }
  }

  /** @param {HTMLButtonElement} button */
  #openNotify(button) {
    const { notifyDialog, notifyThumb, notifyName, notifyEmail, notifySuccess, notifyDone } = this.refs;
    const form = notifyEmail.form;

    this.#notifyHandle = button.dataset.handle ?? '';
    if (button.dataset.thumb) notifyThumb.src = button.dataset.thumb;
    else notifyThumb.removeAttribute('src');
    notifyName.textContent = button.dataset.title ?? '';

    const done = this.#notified.has(this.#notifyHandle);
    if (form) form.hidden = done;
    notifySuccess.replaceChildren(...(done ? [notifyDone.content.cloneNode(true)] : []));
    notifyEmail.value = '';
    this.#setEmailError(false);

    if (!notifyDialog.open) notifyDialog.showModal();
  }

  /** @param {boolean} invalid */
  #setEmailError(invalid) {
    const { notifyEmail, notifyAlert, notifyError } = this.refs;
    notifyEmail.setAttribute('aria-invalid', String(invalid));
    notifyAlert.replaceChildren(...(invalid ? [notifyError.content.cloneNode(true)] : []));
  }

  /**
   * The customer form is posted natively: Shopify's spam protection rejects fetch. The page comes
   * back with a confirmation marker and #showPosted reopens the panel on its success state.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-tmc-notify-form')) return;

    const { notifyEmail, notifyTags } = this.refs;
    if (!EMAIL.test(notifyEmail.value.trim())) {
      event.preventDefault();
      event.stopPropagation();
      this.#setEmailError(true);
      notifyEmail.focus();
      return;
    }

    notifyTags.value = `newsletter,notify-${this.#notifyHandle}`;
    if (this.dataset.notifyLive === 'true') {
      sessionStorage.setItem(NOTIFY_KEY, this.#notifyHandle);
      // A modal dialog would sit above the anti-spam challenge, if Shopify shows one.
      this.refs.notifyDialog.close();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.#notified.add(this.#notifyHandle);
    this.#openNotify(this.#notifyButton(this.#notifyHandle));
  };

  /** @param {string} handle */
  #notifyButton(handle) {
    return /** @type {HTMLButtonElement} */ (this.querySelector(`[data-tmc-notify][data-handle="${CSS.escape(handle)}"]`));
  }

  #showPosted() {
    const handle = sessionStorage.getItem(NOTIFY_KEY);
    sessionStorage.removeItem(NOTIFY_KEY);
    if (!handle || !this.querySelector('[data-tmc-posted]')) return;

    const button = this.#notifyButton(handle);
    if (!button) return;

    // Shopify returns to ?customer_posted=true#contact_form. The browser's jump to that anchor, at the
    // end of parsing, would take the focus out of the panel: drop both from the URL before it happens.
    const url = new URL(location.href);
    url.searchParams.delete('customer_posted');
    history.replaceState(history.state, '', `${url.pathname}${url.search}`);

    this.#notified.add(handle);
    this.#openNotify(button);
    // The trigger was lost with the reload: give focus back to it, not to the body.
    this.refs.notifyDialog.addEventListener('close', () => button.focus(), { once: true });
  }

  /**
   * From 990 px the toast belongs to the floating header (tmd-desktop.js).
   * @param {string | undefined} title
   * @param {string} text
   */
  #toast(title, text) {
    if (window.matchMedia('(min-width: 990px)').matches) {
      document.dispatchEvent(new CustomEvent('tmd:toast', { detail: { title, text } }));
      return;
    }

    const { toast } = this.refs;
    const header = document.querySelector('#header-component');
    if (header) toast.style.setProperty('--tmc-toast-top', `${Math.max(header.getBoundingClientRect().bottom, 0) + 16}px`);

    clearTimeout(this.#toastTimeout);
    const strong = document.createElement('strong');
    strong.textContent = title ?? '';
    const span = document.createElement('span');
    span.textContent = text;
    toast.replaceChildren(strong, span);
    this.#toastTimeout = setTimeout(() => toast.replaceChildren(), TOAST_DURATION);
  }
}

if (!customElements.get('tm-collection')) customElements.define('tm-collection', TmCollection);
